import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * NewStockCardModal — Reçete ekranından çıkmadan yeni Kumaş veya Aksesuar
 * stok kartı oluşturur. Mevcut ERP endpoint'lerini (`/api/inventory/fabrics|accessories`)
 * kullanır. Yeni paralel stok sistemi kurulmaz.
 *
 * Kaydedilince onCreated({kind, id, code, name, unit, ...}) çağrılır.
 */
export default function NewStockCardModal({
  open,
  onClose,
  onCreated,
  initialName = "",
  initialKind = "fabric",
}) {
  const [kind, setKind] = useState(initialKind);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: "",              // boş → backend otomatik verir (KMS-### / AKS-###)
    name: "",
    // Kumaş alanları
    fabric_type: "",
    color: "",
    composition: "",
    weight_gsm: "",
    stock_kg: "0",
    stock_metre: "0",
    min_stock_kg: "0",
    // Aksesuar alanları
    category: "",
    unit: "adet",
    stock: "0",
    min_stock: "0",
    location: "",
  });

  useEffect(() => {
    if (open) {
      setKind(initialKind);
      setForm((f) => ({ ...f, name: initialName, code: "" }));
    }
  }, [open, initialName, initialKind]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Ad zorunlu");
    setSaving(true);
    try {
      const payload = kind === "fabric"
        ? {
            code: form.code.trim(),
            name: form.name.trim(),
            fabric_type: form.fabric_type.trim(),
            color: form.color.trim(),
            composition: form.composition.trim(),
            weight_gsm: Number(form.weight_gsm) || 0,
            stock_kg: Number(form.stock_kg) || 0,
            stock_metre: Number(form.stock_metre) || 0,
            min_stock_kg: Number(form.min_stock_kg) || 0,
            location: form.location.trim(),
          }
        : {
            code: form.code.trim(),
            name: form.name.trim(),
            category: (form.category || "diger").trim(),
            unit: form.unit.trim() || "adet",
            stock: Number(form.stock) || 0,
            min_stock: Number(form.min_stock) || 0,
            location: form.location.trim(),
          };
      const url = kind === "fabric" ? "/inventory/fabrics" : "/inventory/accessories";
      const { data } = await api.post(url, payload);
      toast.success(`${kind === "fabric" ? "Kumaş" : "Aksesuar"} stok kartı oluşturuldu: ${data.code}`);
      // Stok Yönetimi ekranı açıksa otomatik yenile
      window.dispatchEvent(new CustomEvent(
        kind === "fabric" ? "fucan:fabrics-refresh" : "fucan:accessories-refresh"
      ));
      onCreated?.({
        kind,
        id: data.id,
        code: data.code,
        name: data.name,
        unit: kind === "fabric" ? "kg" : data.unit,
        color: data.color,
      });
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e) || "Stok kartı oluşturulamadı");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        data-testid="new-stock-card-modal"
        className="w-[95vw] max-w-[95vw] sm:max-w-xl max-h-[90vh] flex flex-col p-0 gap-0 bg-slate-950 border-slate-800"
      >
        <DialogHeader className="px-4 py-3 border-b border-slate-800 bg-slate-900">
          <DialogTitle className="text-slate-100 text-sm flex items-center gap-2">
            <Plus className="w-4 h-4 text-emerald-400" /> Yeni Stok Kartı Oluştur
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 py-3 overflow-y-auto space-y-3">
          <div className="rounded-sm border border-emerald-900/40 bg-emerald-950/20 p-2 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
            <div className="text-[10px] text-emerald-200 leading-relaxed">
              Bu kart mevcut ERP stok yönetimine kaydedilecek ve reçeteye otomatik bağlanacak.
              Kod boş bırakılırsa sistem otomatik verir (KMS-### / AKS-###).
            </div>
          </div>

          {/* Kind selector */}
          <div className="flex bg-slate-900 border border-slate-800 rounded-sm w-fit">
            {[["fabric", "Kumaş"], ["accessory", "Aksesuar"]].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setKind(k)}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-mono transition-colors ${kind === k ? "bg-blue-600 text-white" : "text-slate-400 hover:text-slate-100"}`}
                data-testid={`new-stock-kind-${k}`}
              >
                {l}
              </button>
            ))}
          </div>

          <Field label="Ad *">
            <Input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="new-stock-name" className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm" />
          </Field>
          <Field label="Stok Kodu (opsiyonel — boş bırak otomatik verilir)">
            <Input value={form.code} onChange={(e) => upd("code", e.target.value.toUpperCase())} placeholder={kind === "fabric" ? "KMS-XXX" : "AKS-XXX"} className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm font-mono" />
          </Field>

          {kind === "fabric" ? (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Tür (Ripstop, Softshell…)">
                  <Input value={form.fabric_type} onChange={(e) => upd("fabric_type", e.target.value)} className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm" />
                </Field>
                <Field label="Renk">
                  <Input value={form.color} onChange={(e) => upd("color", e.target.value)} className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm" />
                </Field>
              </div>
              <Field label="Kompozisyon (%100 pamuk vb.)">
                <Input value={form.composition} onChange={(e) => upd("composition", e.target.value)} className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm" />
              </Field>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Gramaj (g/m²)">
                  <Input type="number" value={form.weight_gsm} onChange={(e) => upd("weight_gsm", e.target.value)} className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm" />
                </Field>
                <Field label="Stok (kg)">
                  <Input type="number" step="0.01" value={form.stock_kg} onChange={(e) => upd("stock_kg", e.target.value)} className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm" />
                </Field>
                <Field label="Stok (m)">
                  <Input type="number" step="0.01" value={form.stock_metre} onChange={(e) => upd("stock_metre", e.target.value)} className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm" />
                </Field>
              </div>
              <Field label="Konum / Depo">
                <Input value={form.location} onChange={(e) => upd("location", e.target.value)} className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm" />
              </Field>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Kategori (Fermuar, Düğme, Etiket…)">
                  <Input value={form.category} onChange={(e) => upd("category", e.target.value)} className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm" />
                </Field>
                <Field label="Birim">
                  <Input value={form.unit} onChange={(e) => upd("unit", e.target.value)} placeholder="adet" className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm" />
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Mevcut Stok">
                  <Input type="number" step="0.01" value={form.stock} onChange={(e) => upd("stock", e.target.value)} className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm" />
                </Field>
                <Field label="Kritik Stok">
                  <Input type="number" step="0.01" value={form.min_stock} onChange={(e) => upd("min_stock", e.target.value)} className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm" />
                </Field>
              </div>
              <Field label="Konum">
                <Input value={form.location} onChange={(e) => upd("location", e.target.value)} className="h-8 bg-slate-900 border-slate-800 text-sm rounded-sm" />
              </Field>
            </>
          )}
        </div>

        <DialogFooter className="border-t border-slate-800 bg-slate-950/80 px-4 py-3 gap-2">
          <Button variant="outline" onClick={() => onClose?.()} className="rounded-sm border-slate-700" data-testid="new-stock-cancel">
            Vazgeç
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !form.name.trim()}
            className="bg-emerald-600 hover:bg-emerald-500 rounded-sm uppercase text-xs font-semibold tracking-wider"
            data-testid="new-stock-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Kaydet ve Reçeteye Bağla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">{label}</div>
      {children}
    </div>
  );
}
